/**
 * AccountSidebar — lista de navegación reusable para CuentaLayout y AdminLayout.
 *
 * Se renderiza idéntico tanto en el sidebar fijo de desktop (≥ lg) como dentro
 * del `<Drawer>` lateral en mobile. La `variant` controla paleta, footer y si
 * incluye el título de módulo (admin).
 *
 * - `cuenta`: paleta clara (cream + primary). Footer = botón "Cerrar sesión".
 * - `admin` : paleta oscura (primary-900 + cream-100). Header con título
 *   "Ceciluz Arte / Panel admin" + footer "Volver al sitio".
 *
 * En mobile (dentro del Drawer) el consumidor inyecta `onItemClick` para
 * cerrar el drawer al navegar. En desktop, se omite.
 */
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { ArrowLeft, LogOut, type LucideIcon } from 'lucide-react';
import { cn } from '../../lib/cn';
import { Routes } from '../../lib/routes';
import { Button } from '../ui/Button';
import { useAuth } from '../../app/auth-context';

export interface AccountSidebarItem {
  to: string;
  label: string;
  /** Icono lucide-react (ForwardRefExoticComponent<LucideProps>). */
  icon: LucideIcon;
  /** `end` para react-router NavLink: matcheo exacto (raíz de la sección). */
  end?: boolean;
}

export type AccountSidebarVariant = 'cuenta' | 'admin';

export interface AccountSidebarProps {
  items: AccountSidebarItem[];
  variant: AccountSidebarVariant;
  /** Llamado tras cada click en un NavLink. Útil para cerrar el Drawer en mobile. */
  onItemClick?: () => void;
  /** Si `false`, omite el footer (sign-out / volver). Default `true`. */
  showFooter?: boolean;
  /** Si `false`, omite la cabecera de marca del admin. Default `true`. */
  showHeader?: boolean;
  className?: string;
}

// Clases por variante (extraídas para evitar branching en el JSX).
const variantClasses = {
  cuenta: {
    root: 'bg-cream-50 text-primary-900',
    linkBase: 'text-primary-900 hover:bg-cream-200',
    linkActive: 'bg-primary-700 text-cream-50 font-semibold',
    divider: 'border-cream-200',
  },
  admin: {
    root: 'bg-primary-900 text-cream-100',
    // QA 2026-06-04: cream-100 (#F7F3ED) seguía percibido como bajo
    // contraste sobre primary-900 (#1A1426) para los items al final
    // del sidebar (Reportes + footer). Subimos al máximo del DS —
    // `cream-50` (#FFFFFF puro, contraste ~17:1) — para los items
    // nav inactivos. La jerarquía del activo se mantiene 100 % por
    // sus 3 señales restantes: `bg-primary-700` + `font-semibold`
    // + `border-l-2 border-gold-500` (color del texto coincide con
    // el inactivo y no es la única señal de estado).
    linkBase: 'text-cream-50 hover:bg-primary-800',
    linkActive: 'bg-primary-700 text-cream-50 font-semibold border-l-2 border-gold-500',
    divider: 'border-primary-800',
  },
} as const;

export function AccountSidebar({
  items,
  variant,
  onItemClick,
  showFooter = true,
  showHeader = true,
  className,
}: AccountSidebarProps) {
  const v = variantClasses[variant];

  return (
    // `min-h-full` garantiza que el contenedor cubra siempre al menos el
    // 100 % de su padre (`<aside>` en desktop, panel del Drawer en mobile).
    // Sin esto, cuando el contenido era más alto que el padre, `h-full`
    // mantenía la altura nominal pero los hijos overflowaban sin bg,
    // dejando ver el cream del layout debajo.
    <div className={cn('flex flex-col h-full min-h-full', v.root, className)}>
      {variant === 'admin' && showHeader && (
        <div className={cn('p-6 border-b', v.divider)}>
          <p className="font-display text-h3">Ceciluz Arte</p>
          <p className="text-caption text-cream-200 uppercase tracking-wider">Panel admin</p>
        </div>
      )}

      <nav
        className={cn('flex-1 p-4', variant === 'cuenta' && 'p-2')}
        aria-label={`Navegación ${variant}`}
      >
        <ul className="flex flex-col gap-1">
          {items.map(({ to, label, icon: Icon, end }) => (
            <li key={to}>
              <NavLink
                to={to}
                end={end}
                onClick={onItemClick}
                className={({ isActive }) =>
                  cn(
                    // `min-h-11` asegura touch target WCAG 2.5.5 sin alterar el padding visual.
                    'flex min-h-11 items-center gap-3 px-4 py-2 rounded',
                    'transition-colors duration-2',
                    isActive ? v.linkActive : v.linkBase,
                  )
                }
              >
                <Icon size={18} strokeWidth={1.5} aria-hidden={true} />
                <span>{label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {showFooter && (
        <div className={cn('p-4 border-t flex flex-col gap-2', v.divider)}>
          {variant === 'cuenta' ? (
            <SignOutButton onAfterSignOut={onItemClick} />
          ) : (
            // Admin: ambas acciones apiladas — "Volver al sitio" mantiene la
            // sesión y sólo navega a la home pública; "Cerrar sesión" hace
            // logout completo en Supabase Auth y redirige al home.
            // Reusamos el patrón visual del `BackToSiteLink` (texto cream
            // sobre primary-900) para que ambas acciones combinen con el
            // sidebar oscuro sin contraste roto. El `SignOutButton` que usa
            // `<Button variant="ghost">` asume fondo claro y vive sólo en
            // `/cuenta`.
            <>
              <BackToSiteLink onClick={onItemClick} />
              <AdminSignOutButton onAfterSignOut={onItemClick} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Subcomponentes internos del footer
// ──────────────────────────────────────────────────────────────────────────

function SignOutButton({ onAfterSignOut }: { onAfterSignOut?: () => void }) {
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    onAfterSignOut?.();
    navigate(Routes.home, { replace: true });
  };

  return (
    <Button
      type="button"
      variant="ghost"
      fullWidth
      leftIcon={<LogOut strokeWidth={1.5} />}
      onClick={handleSignOut}
    >
      Cerrar sesión
    </Button>
  );
}

function BackToSiteLink({ onClick }: { onClick?: () => void }) {
  return (
    <Link
      to={Routes.home}
      onClick={onClick}
      className={cn(
        'flex min-h-11 items-center gap-2 px-4 py-2 rounded',
        // QA 2026-06-04: `cream-50` (#FFFFFF) garantiza legibilidad
        // máxima sobre `primary-900`. Antes `cream-100` se percibía
        // débil al pie del sidebar.
        'text-cream-50 hover:text-gold-500 hover:bg-primary-800',
        'transition-colors duration-2',
      )}
    >
      <ArrowLeft size={16} strokeWidth={1.5} aria-hidden="true" />
      <span className="text-caption">Volver al sitio</span>
    </Link>
  );
}

/**
 * Cerrar sesión del admin — clona el lenguaje visual de `BackToSiteLink`
 * (texto cream sobre fondo violeta, hover gold) para que combine con el
 * sidebar dark del panel admin. Ejecuta `signOut` de Supabase Auth y
 * redirige al home público.
 */
function AdminSignOutButton({ onAfterSignOut }: { onAfterSignOut?: () => void }) {
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    onAfterSignOut?.();
    navigate(Routes.home, { replace: true });
  };

  return (
    <button
      type="button"
      onClick={handleSignOut}
      className={cn(
        'flex min-h-11 w-full items-center gap-2 px-4 py-2 rounded',
        // QA 2026-06-04: `cream-50` (#FFFFFF) garantiza legibilidad
        // máxima sobre `primary-900`. Mismo upgrade que `BackToSiteLink`.
        'text-cream-50 hover:text-gold-500 hover:bg-primary-800',
        'transition-colors duration-2',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-primary-900',
      )}
      aria-label="Cerrar sesión del panel admin"
    >
      <LogOut size={16} strokeWidth={1.5} aria-hidden="true" />
      <span className="text-caption">Cerrar sesión</span>
    </button>
  );
}
